import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import JSZip from 'jszip';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileText, Send, Loader2, AlertTriangle, CheckCircle2, XCircle, Calendar, Download } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' }, { value: '3', label: 'March' },
  { value: '4', label: 'April' }, { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' }, { value: '9', label: 'September' },
  { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

const formatINR = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
};

export const GSTRReportPage = () => {
  const now = new Date();
  const defaultMonth = now.getMonth() === 0 ? '12' : String(now.getMonth());
  const defaultYear = now.getMonth() === 0 ? String(now.getFullYear() - 1) : String(now.getFullYear());

  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [caEmail, setCaEmail] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Send modal
  const [sendModal, setSendModal] = useState(false);
  const [sendReportId, setSendReportId] = useState(null);
  const [sendEmails, setSendEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState('');

  // Confirm regeneration modal
  const [confirmRegenModal, setConfirmRegenModal] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gstr-reports');
      if (res.data.success) setReports(res.data.data);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchCaEmail = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.success) {
        setCaEmail(res.data.data.ca_email || '');
        setCompanyName(res.data.data.company_name || 'Myzow');
      }
    } catch {}
  }, []);

  useEffect(() => { fetchReports(); fetchCaEmail(); }, [fetchReports, fetchCaEmail]);

  const validateSelection = () => {
    const m = parseInt(month);
    const y = parseInt(year);
    if (!m || !y) { toast.error('Please select month and year'); return false; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m >= now.getMonth() + 1)) {
      toast.error('Only past months are allowed');
      return false;
    }
    return true;
  };

  const handleGenerate = async (force = false) => {
    if (!validateSelection()) return;

    const m = parseInt(month);
    const y = parseInt(year);

    if (!force) {
      const existing = reports.find(r => r.month === m && r.year === y);
      if (existing) {
        setConfirmRegenModal(true);
        return;
      }
    }
    setConfirmRegenModal(false);
    setGenerating(true);
    try {
      const res = await api.post('/gstr-reports/generate', { month: m, year: y });
      if (res.data.success) {
        toast.success(`GSTR Report generated: ${res.data.data.invoice_count} invoices found`);
        fetchReports();
      } else {
        toast.error(res.data.detail || 'Generation failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const openSendModal = (report) => {
    setSendReportId(report.id);
    setSendEmails(caEmail);
    setSendModal(true);
    setSendProgress('');
  };

  const [downloading, setDownloading] = useState(null);

  const handleDownloadZip = async (report) => {
    setDownloading(report.id);
    try {
      const invRes = await api.get(`/gstr-reports/invoices?month=${report.month}&year=${report.year}`);
      if (!invRes.data.success || !invRes.data.data.length) {
        toast.error('No invoices found for this period');
        setDownloading(null);
        return;
      }
      const invoices = invRes.data.data;
      const zip = new JSZip();
      for (let i = 0; i < invoices.length; i++) {
        try {
          await api.post(`/invoices/${invoices[i].id}/regenerate-pdf`);
          const resp = await api.get(`/invoices/${invoices[i].id}/pdf`, { responseType: 'blob' });
          const fileName = `${(invoices[i].invoice_number || invoices[i].id).replace(/\//g, '-')}.pdf`;
          zip.file(fileName, resp.data);
        } catch (err) {
          console.error(`PDF fail for ${invoices[i].invoice_number}:`, err);
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const monthLabel = MONTHS[report.month - 1]?.label || report.month;
      const slug = companyName.replace(/\s+/g, '_');
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slug}_GSTR_Invoices_${monthLabel}_${report.year}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${invoices.length} invoice(s) as ZIP`);
    } catch (err) {
      toast.error('Failed to generate ZIP');
    } finally {
      setDownloading(null);
    }
  };

  const handleSend = async () => {
    if (!sendEmails.trim()) { toast.error('Enter at least one email'); return; }
    const report = reports.find(r => r.id === sendReportId);
    if (!report) return;

    setSending(true);
    try {
      // Step 1: Fetch invoice data for the month
      setSendProgress('Fetching invoices...');
      const invRes = await api.get(`/gstr-reports/invoices?month=${report.month}&year=${report.year}`);
      if (!invRes.data.success || !invRes.data.data.length) {
        toast.error('No invoices found for this period');
        setSending(false);
        return;
      }
      const invoices = invRes.data.data;

      // Step 2: Generate PDFs (server-side render + fetch)
      setSendProgress(`Generating ${invoices.length} PDF(s)...`);
      const zip = new JSZip();
      for (let i = 0; i < invoices.length; i++) {
        setSendProgress(`Generating PDF ${i + 1} of ${invoices.length}...`);
        try {
          await api.post(`/invoices/${invoices[i].id}/regenerate-pdf`);
          const resp = await api.get(`/invoices/${invoices[i].id}/pdf`, { responseType: 'blob' });
          const fileName = `${(invoices[i].invoice_number || invoices[i].id).replace(/\//g, '-')}.pdf`;
          zip.file(fileName, resp.data);
        } catch (err) {
          console.error(`Failed to generate PDF for ${invoices[i].invoice_number}:`, err);
        }
      }

      // Step 3: Create ZIP
      setSendProgress('Creating ZIP...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Step 4: Convert to base64 and send
      setSendProgress('Sending email...');
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(zipBlob);
      });

      const res = await api.post(`/gstr-reports/${sendReportId}/send`, {
        emails: sendEmails,
        zip_data: base64,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setSendModal(false);
        fetchReports();
      } else {
        toast.error(res.data.detail || 'Failed to send');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
      setSendProgress('');
    }
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(String(y));

  const monthLabel = MONTHS.find(m => m.value === month)?.label || '';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gstr-report-page">
      <div>
        <h1 className="text-2xl font-bold">GSTR Report</h1>
        <p className="text-muted-foreground">Generate and send tax invoice reports for GST filing</p>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-[160px]" data-testid="gstr-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Year</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[120px]" data-testid="gstr-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => handleGenerate(false)} disabled={generating} data-testid="generate-gstr-btn">
              {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><FileText className="h-4 w-4 mr-2" />Generate GSTR Report</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-5 w-5 text-primary" />Generated Reports</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : reports.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No reports generated yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Generated At</TableHead>
                    <TableHead className="text-center">Invoices</TableHead>
                    <TableHead className="text-right">Gross Amount</TableHead>
                    <TableHead className="text-right">GST Amount</TableHead>
                    <TableHead className="text-right">Grand Total</TableHead>
                    <TableHead className="text-center">Emailed</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map(r => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{MONTHS[r.month - 1]?.label} {r.year}</TableCell>
                      <TableCell className="text-sm">{r.generated_at_local || formatDateTime(r.generated_at)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{r.invoice_count}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatINR(r.total_gross)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatINR(r.total_gst)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{formatINR(r.grand_total)}</TableCell>
                      <TableCell className="text-center">
                        {r.emailed ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Sent</Badge>
                            <span className="text-[10px] text-muted-foreground">{r.emailed_to?.join(', ')}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground"><XCircle className="h-3 w-3 mr-1" />Not Sent</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadZip(r)} disabled={downloading === r.id || r.invoice_count === 0} data-testid={`download-zip-btn-${r.id}`}>
                            {downloading === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openSendModal(r)} data-testid={`send-ca-btn-${r.id}`}>
                            <Send className="h-3.5 w-3.5 mr-1.5" />Send to CA
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Regeneration Modal */}
      <Dialog open={confirmRegenModal} onOpenChange={setConfirmRegenModal}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-regen-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Report Already Exists</DialogTitle>
            <DialogDescription>
              A GSTR report for <strong>{monthLabel} {year}</strong> has already been generated. Generating again will create a new record while preserving the previous one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRegenModal(false)}>Cancel</Button>
            <Button onClick={() => handleGenerate(true)} disabled={generating}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Generate New Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to CA Modal */}
      <Dialog open={sendModal} onOpenChange={(open) => { if (!sending) setSendModal(open); }}>
        <DialogContent className="sm:max-w-lg" data-testid="send-ca-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" />Send GSTR Report to CA</DialogTitle>
            <DialogDescription>
              This will generate PDFs for all tax invoices and email them as a ZIP attachment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Email Addresses</Label>
              <Input
                value={sendEmails}
                onChange={e => setSendEmails(e.target.value)}
                placeholder="ca@example.com, accountant@example.com"
                disabled={sending}
                data-testid="send-ca-emails-input"
              />
              <p className="text-xs text-muted-foreground">Comma-separated for multiple recipients. Pre-filled from Settings.</p>
            </div>
            {sendProgress && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                {sendProgress}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModal(false)} disabled={sending}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="send-now-btn">
              {sending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send Now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
